import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { PopoverBody } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { ReactComponent as SortByIcon } from 'assets/icon-sort-by.svg';
import { ReactComponent as RefreshIcon } from 'assets/icon-refresh.svg';
import { ReactComponent as AddIcon } from 'assets/icon-add-round.svg';
import { ReactComponent as CheckRoundIcon } from 'assets/icon-check-round.svg';
import { ButtonLabelIcon } from 'shared/components/Buttons';
import { LabelIcon } from 'shared/components/Labels';
import { TextField } from 'shared/components/form/Inputs';
import { PopoverSort, OptionMenu } from 'shared/components/SharedStyles/SharedStyles';

import {
  createNewContact,
  refreshContacts,
  searchContacts,
  sortContacts,
} from 'redux/actions/contactActions';

import { colors } from 'shared/defaultTheme';

const ContactsHeader = ({ clientId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { searchValue, sortConfig } = useSelector((state) => state.contact);

  const [orderName, setOrderName] = useState('Name');
  const [isOrder, setIsOrder] = useState(true);

  const onNew = () => dispatch(createNewContact());

  const getSortIcon = (fieldName) => {
    const sortOption = sortConfig.find(
      (currentSortOption) => currentSortOption.field === fieldName
    );
    if (sortOption) {
      return <CheckRoundIcon fill={colors.black} />;
    }
    return <CheckRoundIcon fill={colors.white} />;
  };

  const getOrderIcon = (isSort) => {
    const sortOption = sortConfig.find((currentSortOption) => currentSortOption.asc === isSort);
    if (sortOption) {
      return <CheckRoundIcon fill={colors.black} />;
    }
    return <CheckRoundIcon fill={colors.white} />;
  };

  const onSort = (fieldName, isSort) => {
    dispatch(sortContacts({ fieldName, isSort }));
  };

  useEffect(() => {
    onSort(orderName, isOrder);
  }, [orderName, isOrder]);

  const onRefresh = () => {
    onSort('Name', true);
    dispatch(refreshContacts(true));
  };

  const onChangeSearch = (e) => {
    if (e) {
      const { value } = e.target;
      dispatch(searchContacts(value));
    } else {
      dispatch(searchContacts(''));
    }
  };

  return (
    <>
      <div className="mb-4 d-flex justify-content-end action-header-mobile">
        <div className="form w-25 h-50 d-flex justify-content-end mr-4 w-container-search-mobile">
          <TextField
            borderRadius={2}
            className="m-0 h-50 w-search-contacts"
            inputClassName="w-100"
            withButtonClear
            rightIcon={<SearchIcon fill={colors.black} />}
            value={searchValue ?? ''}
            onChange={onChangeSearch}
            placeholder={t('client_search_contact')}
            isReadOnly={!clientId}
          />
        </div>
        <div className="d-flex btn-action-contacts">
          <ButtonLabelIcon
            id="PopoverSortContact"
            text={t('general_order_by')}
            type="button"
            Icon={() => <SortByIcon />}
            className="button-icon mr-1"
            isDisabled={!clientId}
          />
          {clientId && (
            <PopoverSort trigger="legacy" placement="bottom" target="PopoverSortContact">
              <PopoverBody>
                <OptionMenu onClick={() => setOrderName('Name')} aria-hidden="true">
                  <LabelIcon
                    label={t('clients_name_column')}
                    color=""
                    className="ml-1 body-text--primary action-option-order-label"
                    classNameMain="mb-3 label-icon-sort"
                    isStyle={false}
                    Icon={() => getSortIcon('Name')}
                  />
                </OptionMenu>
                <OptionMenu onClick={() => setOrderName('Surname')} aria-hidden="true">
                  <LabelIcon
                    label={t('general_last_name')}
                    color=""
                    className="ml-1 body-text--primary action-option-order-label"
                    classNameMain="mb-3 label-icon-sort action-option-order-label"
                    isStyle={false}
                    Icon={() => getSortIcon('Surname')}
                  />
                </OptionMenu>
                <OptionMenu onClick={() => setOrderName('Description')} aria-hidden="true">
                  <LabelIcon
                    label={t('general_description')}
                    color=""
                    className="ml-1 body-text--primary action-option-order-label"
                    classNameMain="mb-3 label-icon-sort"
                    isStyle={false}
                    Icon={() => getSortIcon('Description')}
                  />
                </OptionMenu>
                <OptionMenu onClick={() => setOrderName('Phone')} aria-hidden="true">
                  <LabelIcon
                    label={t('general_phone')}
                    color=""
                    className="ml-1 body-text--primary action-option-order-label"
                    classNameMain="mb-3 label-icon-sort"
                    isStyle={false}
                    Icon={() => getSortIcon('Phone')}
                  />
                </OptionMenu>
                <OptionMenu onClick={() => setOrderName('Mail')} aria-hidden="true">
                  <LabelIcon
                    label={t('general_email')}
                    color=""
                    className="ml-1 body-text--primary action-option-order-label"
                    classNameMain="mb-3 label-icon-sort"
                    isStyle={false}
                    Icon={() => getSortIcon('Mail')}
                  />
                </OptionMenu>
                <hr />
                <OptionMenu onClick={() => setIsOrder(true)}>
                  <LabelIcon
                    label={t('general_ascending')}
                    color=""
                    className="ml-1 body-text--primary action-option-order-label"
                    classNameMain="mb-3 label-icon-sort"
                    isStyle={false}
                    Icon={() => getOrderIcon(true)}
                  />
                </OptionMenu>
                <OptionMenu onClick={() => setIsOrder(false)}>
                  <LabelIcon
                    label={t('general_descending')}
                    color=""
                    className="ml-1 body-text--primary action-option-order-label"
                    classNameMain="mb-3 label-icon-sort"
                    isStyle={false}
                    Icon={() => getOrderIcon(false)}
                  />
                </OptionMenu>
                <OptionMenu onClick={() => setIsOrder('none')}>
                  <LabelIcon
                    label={t('general_none')}
                    color=""
                    className="ml-1 body-text--primary action-option-order-label"
                    classNameMain="mb-3 label-icon-sort"
                    isStyle={false}
                    Icon={() => getOrderIcon('none')}
                  />
                </OptionMenu>
              </PopoverBody>
            </PopoverSort>
          )}

          <ButtonLabelIcon
            id="refres-icon"
            text={t('general_button_refresh')}
            type="button"
            Icon={() => <RefreshIcon />}
            className="button-icon mr-1"
            onClick={onRefresh}
            isDisabled={!clientId}
          />
          <ButtonLabelIcon
            id="add-icon"
            text={t('general_button_new')}
            type="button"
            Icon={() => <AddIcon />}
            className="button-icon"
            onClick={onNew}
          />
        </div>
      </div>
    </>
  );
};

ContactsHeader.defaultProps = {
  clientId: undefined,
};

ContactsHeader.propTypes = {
  clientId: PropTypes.string,
};

export default ContactsHeader;
